export type ArchiveState =
  | "idle"
  | "capturing"
  | "pending_finalization"
  | "ready_to_upload"
  | "uploading"
  | "uploaded"
  | "committed"
  | "failed";

export interface SessionManifest {
  sessionId: string;
  streamEpoch: string;
  startedAt: number;
  endedAt?: number;
  sampleRate: 16000;
  channels: 1;
  encoding: "pcm_s16le";
  totalSegments: number;
  totalBytes: number;
  totalDurationMs: number;
  lastAssignedSeq: number;
  lastDurableSeq: number;
  lastSentSeq: number;
  hardCommittedMs: number;
  softCommittedMs: number;
  lastCommitAdvanceAt?: number;
  replayFromSeq?: number;
  replayInFlight: boolean;
  reconnectCount: number;
  archiveState: ArchiveState;
  archiveUploadId?: string;
  archiveCommittedAt?: number;
  storagePersistenceGranted?: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface AudioSegment {
  id?: number;
  sessionId: string;
  segmentId: number;
  startSeq: number;
  endSeq: number;
  startMs: number;
  endMs: number;
  durationMs: number;
  byteLength: number;
  chunkCount: number;
  payloadKey: string;
  storageKind: "idb" | "opfs";
  checksum32?: number;
  keepForReplay: boolean;
  keepForArchive: boolean;
  createdAt: number;
  uploadedAt?: number;
  deletedAt?: number;
}

export interface ChunkIndexRow {
  sessionId: string;
  seq: number;
  chunkId: string;
  segmentId: number;
  startMs: number;
  endMs: number;
  durationMs: number;
  offsetBytes: number;
  byteLength: number;
}

export interface RecoveryCheckpoint {
  sessionId: string;
  hardCommittedMs: number;
  softCommittedMs: number;
  lastFinalCoverageMs: number;
  lastStablePartialCoverageMs: number;
  lastCommitAdvanceAt?: number;
  suspectedGapFromSeq?: number;
}

export interface DiagnosticEvent {
  id?: number;
  sessionId: string;
  ts: number;
  code: string;
  detail?: Record<string, unknown>;
}

export interface PayloadRecord {
  payloadKey: string;
  sessionId: string;
  segmentId: number;
  byteLength: number;
  checksum32?: number;
  payload: Blob;
  createdAt: number;
}

export interface CaptureChunk {
  seq: number;
  chunkId: string;
  startMs: number;
  endMs: number;
  durationMs: number;
  bytes: Uint8Array;
}

export interface BuiltSegment {
  segmentId: number;
  startSeq: number;
  endSeq: number;
  startMs: number;
  endMs: number;
  durationMs: number;
  chunkCount: number;
  bytes: Uint8Array;
  chunkIndexRows: ChunkIndexRow[];
}

export interface ConsultationStorageStatus {
  sessionId: string;
  archiveState: ArchiveState;
  durableSegments: number;
  durableBytes: number;
  totalDurationMs: number;
  hardCommittedMs: number;
  softCommittedMs: number;
  replayFromMs: number;
  replayFromSeq: number;
  replayInFlight: boolean;
  backlogMs: number;
  backlogBytes: number;
  lastDurableSeq: number;
  lastAssignedSeq: number;
  lastSentSeq: number;
  reconnectCount: number;
  updatedAt: number;
}

export interface CoverageState {
  hardCommittedMs: number;
  softCommittedMs: number;
  lastFinalCoverageMs: number;
  lastStablePartialCoverageMs: number;
  lastCommitAdvanceAt: number | null;
  updated: boolean;
}

export interface ReplayPlan {
  committedMs: number;
  replayFromMs: number;
  replayFromSeq: number;
}

export interface ReplayBatch {
  segments: AudioSegment[];
  payloads: Array<{ segmentId: number; payload: ArrayBuffer }>;
  nextSeq: number;
  done: boolean;
}
